import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ExternalLink, Mail, DollarSign, Heart, Camera, Film, User, Clock } from 'lucide-react';
import './App.css';

// Image constants
const HERO_IMAGE = '/images/sharp.jpg';
const STARE_IMAGE = '/images/thestare.jpg';
const SERVING_IMAGE = '/images/serving.jpg';
const ABOUT_IMAGE = '/images/aboutme.png';
const FILMCREW_IMAGE = '/images/filmcrew.jpg';

// Funding deadline
const FUNDING_DEADLINE = new Date('2026-07-10T23:59:59');

// Pitch video (hosted externally to avoid GitHub file size limits)
const PITCH_VIDEO_URL = "https://customer-assets.emergentagent.com/job_showcase-pitch-hub/artifacts/j411h8vw_f6a87b64-5010-4dda-9c21-0908145adb57.mp4";
const PITCH_THUMBNAIL = "/images/pitch-thumbnail.png";

// Calculate days until deadline
const getDaysUntilDeadline = () => {
  const now = new Date();
  const diff = FUNDING_DEADLINE - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

// Gallery images data - organized by outfit/look (6 rows based on folders)
// Row 1: Folder 2 - Red outfit
const row1Images = [
  { src: '/images/sharp.jpg', alt: 'Lady Renaissance in red leather outfit editorial' },
  { src: '/images/thestare.jpg', alt: 'Lady Renaissance dramatic stare in red outfit' },
  { src: '/images/serving.jpg', alt: 'Lady Renaissance glamour shot in red' },
  { src: '/images/501668012185058269440105352598855913852912225n.jpg', alt: 'Lady Renaissance red outfit portrait' },
];

// Row 2: Folder 3 - Dominatrix look (gold and black corset)
const row2Images = [
  { src: '/images/mic_drop.jpg', alt: 'Lady Renaissance in gold and black corset' },
  { src: '/images/serving_face.jpg', alt: 'Lady Renaissance dominatrix look artistic portrait' },
  { src: '/images/491423242185000492380105354328454796080047137n.jpg', alt: 'Lady Renaissance dominatrix style portrait' },
];

// Row 3: Folder 4 - 70s afro portraits
const row3Images = [
  { src: '/images/Inthegazeoftheeyesstoriesarewrittenwithoutasingleword.jpg', alt: 'Lady Renaissance 70s afro expressive gaze' },
  { src: '/images/Stuckinthe70s.jpg', alt: 'Lady Renaissance stuck in the 70s style' },
  { src: '/images/Theeyesarethewindowstothesoulcapturingemotionsthatwordscanneverexpress.jpg', alt: 'Lady Renaissance 70s afro soulful portrait' },
];

// Row 4: Folder 5 - Emo look with bantu knots
const row4Images = [
  { src: '/images/Eyesholdstoriesthatwordscouldnevertell.jpg', alt: 'Lady Renaissance with bantu knots close-up' },
  { src: '/images/EmotionsrawbuttheymakemewhoIam.jpg', alt: 'Lady Renaissance emotional portrait bantu knots' },
  { src: '/images/BurningonJustlikethematchyoustriketoincinerate.jpg', alt: 'Lady Renaissance dramatic emo portrait' },
  { src: '/images/JustliketheHearseyoudietogetinagainWearesofarfromyou.jpg', alt: 'Lady Renaissance emo artistic portrait' },
];

// Row 5: Folder 6 - 70s afro leopard/gold looks
const row5Images = [
  { src: '/images/audrianna_furs.jpg', alt: 'Lady Renaissance 70s afro in leopard fur coat' },
  { src: '/images/luck.jpg', alt: 'Lady Renaissance 70s afro in gold sequin dress' },
  { src: '/images/491425326184976081080105354673627342507760308n.jpg', alt: 'Lady Renaissance 70s afro creative portrait' },
];

// Row 6: Folder 7 - All-pink look
const row6Images = [
  { src: '/images/Dressinginpinkbecauselifeismeanttopop.jpg', alt: 'Lady Renaissance pink fashion editorial' },
  { src: '/images/Pinkispowerstrengthwrappedinsoftness.jpg', alt: 'Lady Renaissance pink power portrait' },
  { src: '/images/Styleissimplebutchicisinthedetails.jpg', alt: 'Lady Renaissance pink chic style' },
];

// Navigation Component with Dropdown
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileShowDropdown, setMobileShowDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/modeling', label: 'Modeling' },
  ];

  const showLinks = [
    { path: '/corporate-chaos', label: 'Corporate Chaos' },
    { path: '/support', label: 'Support the Show' },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#1a1614]/95 backdrop-blur-md py-4 border-b border-[#c9a962]/20"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="font-display text-2xl text-gold-gradient" data-testid="logo-link" aria-label="Lady Renaissance - Home">
          Lady Renaissance
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8" role="menubar">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
              role="menuitem"
              aria-current={location.pathname === link.path ? 'page' : undefined}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Explore Show Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => setShowDropdown(true)}
            onMouseLeave={() => setShowDropdown(false)}
          >
            <Link
              to="/corporate-chaos"
              data-testid="nav-explore-show"
              className={`nav-link inline-flex items-center gap-1 ${
                location.pathname === '/corporate-chaos' || location.pathname === '/support' ? 'active' : ''
              }`}
              role="menuitem"
              aria-haspopup="true"
              aria-expanded={showDropdown}
            >
              Explore the Show
              <ChevronDown size={16} className={`transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} aria-hidden="true" />
            </Link>
            
            {/* Dropdown Menu */}
            <div className={`nav-dropdown ${showDropdown ? 'show' : ''}`} role="menu" aria-label="Show submenu">
              <div className="py-2">
                {showLinks.map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`nav-dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                    role="menuitem"
                    aria-current={location.pathname === link.path ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link
            to="/contact"
            data-testid="nav-contact"
            className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
            role="menuitem"
            aria-current={location.pathname === '/contact' ? 'page' : undefined}
          >
            Contact
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#dbd0c2] p-2"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isOpen}
          aria-controls="mobile-menu"
        >
          {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div id="mobile-menu" className="mobile-menu md:hidden absolute top-full left-0 right-0 py-8" role="menu">
          <div className="flex flex-col items-center gap-6">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`nav-link text-lg ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
                role="menuitem"
                aria-current={location.pathname === link.path ? 'page' : undefined}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Mobile Explore Show Dropdown */}
            <div className="flex flex-col items-center">
              <button
                className={`nav-link text-lg inline-flex items-center gap-1 ${
                  location.pathname === '/corporate-chaos' || location.pathname === '/support' ? 'active' : ''
                }`}
                onClick={() => setMobileShowDropdown(!mobileShowDropdown)}
              >
                Explore the Show
                <ChevronDown size={16} className={`transition-transform duration-200 ${mobileShowDropdown ? 'rotate-180' : ''}`} />
              </button>
              {mobileShowDropdown && (
                <div className="flex flex-col items-center gap-3 mt-3 pl-4 border-l-2 border-[#c9a962]/30">
                  {showLinks.map(link => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`nav-link text-base ${location.pathname === link.path ? 'active' : ''}`}
                      onClick={() => { setIsOpen(false); setMobileShowDropdown(false); }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/contact"
              data-testid="mobile-nav-contact"
              className={`nav-link text-lg ${location.pathname === '/contact' ? 'active' : ''}`}
              onClick={() => setIsOpen(false)}
            >
              Contact
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-gradient-to-b from-[#0f0d0c] to-[#1a1614] py-16 border-t border-[#c9a962]/30" data-testid="footer" role="contentinfo">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid md:grid-cols-3 gap-12 mb-12">
        <div>
          <h3 className="font-display text-3xl text-gold-gradient mb-4">Lady Renaissance</h3>
          <p className="text-[#dbd0c2]/80 text-sm leading-relaxed">
            Model, creative visionary, and producer. From being in front of the camera to helping shape what's behind it, 
            redefining what it means to be a creative force in the modern era.
          </p>
        </div>
        <div>
          <h4 className="font-display text-xl text-[#c9a962] mb-4">Quick Links</h4>
          <nav className="flex flex-col gap-3" aria-label="Footer navigation">
            <Link to="/about" className="text-[#dbd0c2]/80 hover:text-[#e8d5a3] transition-colors">About</Link>
            <Link to="/modeling" className="text-[#dbd0c2]/80 hover:text-[#e8d5a3] transition-colors">Modeling Portfolio</Link>
            <Link to="/corporate-chaos" className="text-[#dbd0c2]/80 hover:text-[#e8d5a3] transition-colors">Corporate Chaos</Link>
            <Link to="/support" className="text-[#dbd0c2]/80 hover:text-[#e8d5a3] transition-colors">Support the Show</Link>
          </nav>
        </div>
        <div>
          <h4 className="font-display text-xl text-[#c9a962] mb-4">Connect</h4>
          <Link to="/contact" className="btn-outline inline-block" data-testid="footer-contact-btn">
            Get in Touch
          </Link>
          <p className="mt-4 text-[#dbd0c2]/60 text-sm">
            chifuninthecityllc.com
          </p>
        </div>
      </div>
      <div className="border-t border-[#c9a962]/20 pt-8 text-center">
        <p className="text-[#dbd0c2]/60 text-sm">
          <span aria-label="Copyright">&copy;</span> {new Date().getFullYear()} Lady Renaissance / Chi Fun in the City LLC. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Home Page
const HomePage = () => (
  <main className="page-transition" data-testid="home-page" id="main-content" role="main">
    {/* Hero Section */}
    <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section" aria-label="Hero section">
      <div className="absolute inset-0">
        <img 
          src={HERO_IMAGE} 
          alt="Lady Renaissance, professional model and creative visionary, in an elegant editorial portrait"
          className="w-full h-full object-cover object-center"
        />
        <div className="hero-gradient absolute inset-0" aria-hidden="true"></div>
      </div>
      
      {/* Top tagline - positioned at top right */}
      <div className="absolute top-24 right-0 z-10">
        <p className="bg-[#1a1614]/80 backdrop-blur-sm text-[#c9a962] uppercase tracking-[4px] text-xs md:text-sm py-2 px-4 md:py-3 md:px-6 inline-block rounded-l-sm border border-r-0 border-[#c9a962]/30 animate-fade-in" role="doc-subtitle">Model &bull; Creative Visionary &bull; Producer</p>
      </div>
      
      {/* Main content - centered lower */}
      <div className="relative z-10 text-center px-6 max-w-4xl mt-20">
        <h1 className="font-display text-6xl md:text-8xl text-gold-gradient mb-6 animate-fade-in-up drop-shadow-lg">
          Lady Renaissance
        </h1>
        <p className="text-[#dbd0c2] text-lg md:text-xl animate-fade-in-up animation-delay-200 bg-[#1a1614]/60 backdrop-blur-sm py-2 px-4 inline-block rounded-sm">
          Brand. Vision. Evolution.
        </p>
      </div>
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
        <ChevronDown className="text-[#c9a962]" size={32} aria-label="Scroll down" />
      </div>
    </section>

    {/* Introduction Section */}
    <section className="py-24 px-6 bg-gradient-to-b from-[#1a1614] via-[#2d2420] to-[#1a1614] relative overflow-hidden" data-testid="intro-section" aria-labelledby="intro-heading">
      {/* Decorative accent stripe */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6F2735] via-[#C8A24E] to-[#6F2735]" aria-hidden="true"></div>
      <div className="max-w-4xl mx-auto text-center">
        <h2 id="intro-heading" className="font-display text-4xl md:text-5xl text-[#dbd0c2] mb-6">
          A Modern Creative Powerhouse
        </h2>
        <div className="section-divider mb-8" aria-hidden="true"></div>
        <p className="text-[#dbd0c2]/90 text-lg leading-relaxed">
          From being in front of the camera to helping shape what's behind it, Lady Renaissance is redefining what it means to be a creative force 
          in the modern era. Model, storyteller, and visionary behind Chi Fun in the City.
        </p>
      </div>
    </section>

    {/* Featured Sections */}
    <section className="py-16 px-6 bg-gradient-to-b from-[#0f0d0c] via-[#6F2735]/20 to-[#0f0d0c]" data-testid="featured-sections">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Modeling */}
          <Link to="/modeling" className="card-heritage group rounded-sm overflow-hidden" data-testid="featured-modeling">
            <div className="image-hover-zoom aspect-[3/4]">
              <img src={SERVING_IMAGE} alt="Lady Renaissance Modeling Portfolio - Editorial and Beauty" className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 text-[#c9a962] mb-2">
                <Camera size={18} />
                <span className="text-sm uppercase tracking-wider">Portfolio</span>
              </div>
              <h3 className="font-display text-2xl text-[#dbd0c2] mb-2">Modeling</h3>
              <p className="text-[#dbd0c2]/60 text-sm">Editorial, Beauty, Lifestyle & More</p>
            </div>
          </Link>

          {/* Corporate Chaos */}
          <Link to="/corporate-chaos" className="card-heritage group rounded-sm overflow-hidden" data-testid="featured-corporate-chaos">
            <div className="image-hover-zoom aspect-[3/4]">
              <img src={FILMCREW_IMAGE} alt="Corporate Chaos - Docu-style Reality Series Cast and Crew" className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 text-[#c9a962] mb-2">
                <Film size={18} />
                <span className="text-sm uppercase tracking-wider">The Show</span>
              </div>
              <h3 className="font-display text-2xl text-[#dbd0c2] mb-2">Corporate Chaos</h3>
              <p className="text-[#dbd0c2]/60 text-sm">The Vision Behind the Chaos</p>
            </div>
          </Link>

          {/* About */}
          <Link to="/about" className="card-heritage group rounded-sm overflow-hidden" data-testid="featured-about">
            <div className="image-hover-zoom aspect-[3/4]">
              <img src={ABOUT_IMAGE} alt="About Lady Renaissance - Creative Visionary" className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 text-[#c9a962] mb-2">
                <User size={18} />
                <span className="text-sm uppercase tracking-wider">Story</span>
              </div>
              <h3 className="font-display text-2xl text-[#dbd0c2] mb-2">About Me</h3>
              <p className="text-[#dbd0c2]/60 text-sm">The Brand. The Movement. The Evolution.</p>
            </div>
          </Link>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-24 px-6 text-center bg-gradient-to-b from-[#1a1614] via-[#A88E7B]/10 to-[#1a1614] relative" data-testid="cta-section">
      {/* Decorative borders */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#A88E7B]/40 to-transparent"></div>
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#6F2735]/40 to-transparent"></div>
      <div className="max-w-3xl mx-auto">
        <h2 className="font-display text-4xl md:text-5xl text-[#dbd0c2] mb-6">
          Let&apos;s Create Something Extraordinary
        </h2>
        <p className="text-[#dbd0c2]/80 mb-10">
          Available for modeling campaigns, brand collaborations, and creative projects.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" className="btn-gold" data-testid="cta-contact-btn">Get in Touch</Link>
          <Link to="/support" className="btn-outline" data-testid="cta-support-btn">Support the Show</Link>
        </div>
      </div>
    </section>
  </main>
);

// About Page
const AboutPage = () => (
  <main className="page-transition pt-24" data-testid="about-page" id="main-content" role="main">
    <section className="py-16 px-6" aria-labelledby="about-heading">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="image-hover-zoom rounded-sm overflow-hidden">
            <img 
              src={ABOUT_IMAGE} 
              alt="Lady Renaissance, professional model and creative visionary, wearing elegant white attire with natural afro hairstyle"
              className="w-full aspect-[3/4] object-cover"
            />
          </div>
          <div>
            <p className="text-[#c9a962] uppercase tracking-[4px] text-sm mb-4">About</p>
            <h1 id="about-heading" className="font-display text-5xl md:text-6xl text-gold-gradient mb-6">
              Lady Renaissance
            </h1>
            <div className="section-divider mb-8 mx-0" aria-hidden="true"></div>
            <div className="space-y-6 text-[#dbd0c2]/80 leading-relaxed">
              <p>
                Lady Renaissance is a model, creative visionary, and the driving force behind 
                <strong className="text-[#c9a962]"> Chi Fun in the City LLC</strong>. With a unique blend of artistry and ambition, 
                she moves seamlessly between the worlds of fashion and film.
              </p>
              <p>
                From editorial shoots to producing compelling content, Lady Renaissance represents 
                the evolution of the modern creative: bold, multifaceted, and unapologetically authentic. 
                Her work explores themes of identity, resilience, and transformation.
              </p>
              <p>
                As the visionary behind <strong className="text-[#c9a962]">Corporate Chaos</strong>, she&apos;s channeling 
                her creative energy into telling stories that matter, stories about the human experience 
                in corporate America and the courage it takes to find oneself within it.
              </p>
              <p className="text-[#c9a962] font-display text-xl italic">
                &quot;The Brand. The Movement. The Evolution.&quot;
              </p>
            </div>
            <div className="mt-10 flex gap-4">
              <Link to="/modeling" className="btn-gold" data-testid="about-portfolio-btn">View Portfolio</Link>
              <Link to="/corporate-chaos" className="btn-outline" data-testid="about-show-btn">The Show</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
);

// Modeling Page - Organized by outfit/look rows
const ModelingPage = () => {
  // Define gallery rows by outfit (6 rows based on folder structure)
  const galleryRows = [
    row1Images,  // Red outfit
    row2Images,  // Dominatrix look
    row3Images,  // 70s afro portraits
    row4Images,  // Emo look with bantu knots
    row5Images,  // 70s afro leopard/gold
    row6Images,  // All-pink look
  ];

  return (
    <main className="page-transition pt-24" data-testid="modeling-page" id="main-content" role="main">
      <section className="py-16 px-6" aria-labelledby="modeling-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#c9a962] uppercase tracking-[4px] text-sm mb-4">Portfolio</p>
            <h1 id="modeling-heading" className="font-display text-5xl md:text-6xl text-gold-gradient mb-6">
              Creative Collaborations
            </h1>
            <div className="section-divider mb-8" aria-hidden="true"></div>
            <p className="text-[#dbd0c2]/70 max-w-2xl mx-auto">
              Editorial, beauty, lifestyle, and creative expression. Each image tells a story of transformation and artistry.
            </p>
          </div>

          {/* Gallery Rows */}
          {galleryRows.map((rowImages, rowIndex) => (
            <div key={rowIndex}>
              <GalleryRow images={rowImages} />
              {rowIndex < galleryRows.length - 1 && <GalleryDivider />}
            </div>
          ))}

          {/* Contact CTA */}
          <section className="text-center mt-16 py-12 cta-card-enhanced rounded-sm" aria-labelledby="booking-heading">
            <h2 id="booking-heading" className="font-display text-3xl text-[#dbd0c2] mb-4">Interested in Booking?</h2>
            <p className="text-[#dbd0c2]/70 mb-6">Available for modeling campaigns and creative collaborations.</p>
            <Link to="/contact" className="btn-gold" data-testid="modeling-contact-btn">Get in Touch</Link>
          </section>
        </div>
      </section>
    </main>
  );
};

// Gallery Row Component (no title, just images in a row)
const GalleryRow = ({ images }) => {
  // Dynamically determine grid columns based on image count
  const cols = images.length;
  const gridClass = cols >= 5 ? 'grid-cols-2 md:grid-cols-5' : 
                    cols === 4 ? 'grid-cols-2 md:grid-cols-4' : 
                    cols === 3 ? 'grid-cols-1 md:grid-cols-3' :
                    cols === 2 ? 'grid-cols-2' :
                    'grid-cols-1';
  return (
    <section className="mb-12">
      <div className={`grid gap-6 ${gridClass}`}>
        {images.map((img, idx) => (
          <article key={idx} className="gallery-card group">
            <div className="image-hover-zoom">
              <img src={img.src} alt={img.alt} className="w-full aspect-[3/4] object-cover" loading="lazy" />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

// Gallery Divider Component
const GalleryDivider = () => (
  <div className="flex items-center justify-center gap-4 mb-12" aria-hidden="true">
    <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#3a1f1f] to-[#c9a962]"></div>
    <Camera className="text-[#c9a962]" size={20} />
    <div className="h-px w-24 bg-gradient-to-l from-transparent via-[#3a1f1f] to-[#c9a962]"></div>
  </div>
);

// Corporate Chaos Page
const CorporateChaosPage = () => (
  <main className="page-transition pt-24" data-testid="corporate-chaos-page" id="main-content" role="main">
    {/* Hero */}
    <section className="relative py-32 px-6 overflow-hidden" aria-labelledby="chaos-heading">
      <div className="absolute inset-0">
        <img src={FILMCREW_IMAGE} alt="Corporate Chaos cast and crew group photo" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1614] via-[#1a1614]/80 to-[#1a1614]" aria-hidden="true"></div>
      </div>
      <div className="relative max-w-4xl mx-auto text-center">
        <p className="text-[#c9a962] uppercase tracking-[4px] text-sm mb-4">The Show</p>
        <h1 id="chaos-heading" className="font-display text-5xl md:text-7xl text-gold-gradient mb-8">
          Corporate Chaos
        </h1>
        <div className="section-divider mb-8" aria-hidden="true"></div>
        <p className="text-[#dbd0c2] text-xl md:text-2xl leading-relaxed font-display italic">
          Corporate Chaos is a docu-style reality series exploring the human cost of corporate life 
          through the stories of six individuals navigating work, identity, and survival in different ways.
        </p>
      </div>
    </section>

    {/* About the Show */}
    <section className="py-20 px-6 bg-[#0f0d0c]" data-testid="show-details" aria-labelledby="vision-heading">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <h2 id="vision-heading" className="font-display text-4xl text-[#dbd0c2] mb-6">The Vision</h2>
            <div className="space-y-4 text-[#dbd0c2]/80 leading-relaxed">
              <p>
                In a world where corporate life often demands we sacrifice our authentic selves, 
                <strong className="text-[#c9a962]"> Corporate Chaos</strong> dares to look beneath the surface.
              </p>
              <p>
                This isn&apos;t just another workplace drama. It&apos;s a raw, honest exploration of what it means 
                to survive and sometimes thrive in environments that weren&apos;t designed with humanity in mind.
              </p>
              <p>
                Through the lens of six diverse individuals, we witness the struggles, triumphs, 
                and transformations that come from navigating the corporate landscape while staying true to oneself.
              </p>
            </div>
          </div>
          <div>
            <h2 className="font-display text-4xl text-[#dbd0c2] mb-6">Support the Project</h2>
            <div className="space-y-4 text-[#dbd0c2]/80 leading-relaxed">
              <p>
                Corporate Chaos is an independent production powered by passionate storytellers 
                and supporters who believe in authentic representation.
              </p>
              <p>
                Your contribution helps bring these important stories to life. Every dollar goes directly to 
                production, from film crew to post production.
              </p>
              <div className="mt-8">
                <Link to="/support" className="btn-gold" data-testid="chaos-support-btn">
                  Support Corporate Chaos
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Pitch Video Section */}
    <section className="py-20 px-6 bg-[#1a1614]" data-testid="pitch-video-section" aria-labelledby="pitch-heading">
      <div className="max-w-4xl mx-auto text-center">
        <h2 id="pitch-heading" className="font-display text-4xl text-[#dbd0c2] mb-4">The Pitch</h2>
        <p className="text-[#dbd0c2]/70 mb-8">
          Hear directly from us about why Corporate Chaos matters.
        </p>
        <div className="relative rounded-lg overflow-hidden shadow-2xl border border-[#c9a962]/20">
          <video 
            controls
            className="w-full aspect-video bg-black"
            poster={PITCH_THUMBNAIL}
            preload="metadata"
            data-testid="pitch-video"
            aria-label="Corporate Chaos pitch video"
          >
            <source src={PITCH_VIDEO_URL} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </section>

    {/* Seed & Spark CTA */}
    <section className="py-20 px-6" data-testid="seedspark-section" aria-labelledby="movement-heading">
      <div className="max-w-3xl mx-auto text-center">
        <h2 id="movement-heading" className="font-display text-4xl text-[#dbd0c2] mb-6">Join the Movement</h2>
        <p className="text-[#dbd0c2]/70 mb-8">
          Be part of the story, crowdfunding our independent series today.
        </p>
        <a 
          href="https://seedandspark.com/fund/corporatechaos#story" 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn-gold inline-flex items-center gap-2"
          data-testid="seedspark-link"
          aria-label="Visit our Seed and Spark crowdfunding campaign (opens in new tab)"
        >
          Visit Our Campaign <ExternalLink size={18} aria-hidden="true" />
        </a>
      </div>
    </section>
  </main>
);

// Support Page
const SupportPage = () => {
  const [daysLeft, setDaysLeft] = useState(getDaysUntilDeadline());

  useEffect(() => {
    const timer = setInterval(() => {
      setDaysLeft(getDaysUntilDeadline());
    }, 86400000); // Update daily
    return () => clearInterval(timer);
  }, []);

  const budgetItems = [
    { name: 'Film Crew', percentage: 34, amount: 9307.50 },
    { name: 'Makeup Artist', percentage: 13.7, amount: 3750.38 },
    { name: 'Legal Fees', percentage: 12.8, amount: 3504 },
    { name: 'Locations', percentage: 11, amount: 3011.25 },
    { name: 'Cast', percentage: 8.8, amount: 2409 },
    { name: 'Crew Food', percentage: 7.3, amount: 1998.38 },
    { name: 'Wardrobe Stylist', percentage: 6.2, amount: 1697.25 },
    { name: 'Transportation', percentage: 3.7, amount: 1012.88 },
    { name: 'Camera Equipment', percentage: 1.1, amount: 301.13 },
    { name: 'Sound Gear', percentage: 0.8, amount: 219 },
    { name: 'Permits', percentage: 0.7, amount: 191.63 },
  ];

  return (
    <main className="page-transition pt-24" data-testid="support-page" id="main-content" role="main">
      {/* Hero */}
      <section className="py-16 px-6 text-center" aria-labelledby="support-heading">
        <p className="text-[#c9a962] uppercase tracking-[4px] text-sm mb-4">Support</p>
        <h1 id="support-heading" className="font-display text-5xl md:text-6xl text-gold-gradient mb-6">
          Support Corporate Chaos
        </h1>
        <div className="section-divider mb-8" aria-hidden="true"></div>
        <p className="text-[#dbd0c2]/70 max-w-2xl mx-auto">
          Your contribution helps bring authentic stories to life. Every donation goes directly into production.
        </p>
      </section>

      {/* Funding Deadline Banner */}
      <section className="py-8 px-6 bg-gradient-to-r from-[#6F2735] via-[#2d2420] to-[#6F2735]" data-testid="deadline-banner" aria-label="Funding deadline information">
        <div className="max-w-4xl mx-auto">
          <div className="deadline-card" role="alert">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
              <div className="flex items-center gap-3">
                <Clock className="text-[#c9a962]" size={28} aria-hidden="true" />
                <span className="text-[#dbd0c2] uppercase tracking-wider text-sm font-semibold">Funding Deadline</span>
              </div>
              <div className="text-center">
                <p className="font-display text-3xl md:text-4xl text-gold-gradient"><time dateTime="2026-07-10">July 10, 2026</time></p>
              </div>
              <div className="deadline-countdown">
                <span className="font-display text-3xl text-[#e8d5a3]" aria-label={`${daysLeft} days remaining`}>{daysLeft}</span>
                <span className="text-[#dbd0c2]/70 text-sm uppercase tracking-wider ml-2">Days Left</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Options */}
      <section className="py-16 px-6 bg-[#0f0d0c]" data-testid="donation-options" aria-labelledby="donate-heading">
        <div className="max-w-6xl mx-auto">
          <h2 id="donate-heading" className="font-display text-3xl text-[#dbd0c2] text-center mb-12">Ways to Donate</h2>
          <div className="grid md:grid-cols-3 gap-8" role="list" aria-label="Donation options">
            {/* Venmo */}
            <article className="donation-card rounded-sm" data-testid="venmo-option" role="listitem">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#c9a962]/20 flex items-center justify-center" aria-hidden="true">
                <DollarSign className="text-[#c9a962]" size={32} />
              </div>
              <h3 className="font-display text-2xl text-[#dbd0c2] mb-4">Venmo</h3>
              <p className="text-[#c9a962] font-mono text-lg mb-4 break-all">
                $ChiFunInTheCityLLC25
              </p>
              <p className="text-[#dbd0c2]/60 text-sm">
                Quick and easy mobile payments
              </p>
            </article>

            {/* Zelle */}
            <article className="donation-card rounded-sm" data-testid="zelle-option" role="listitem">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#c9a962]/20 flex items-center justify-center" aria-hidden="true">
                <Mail className="text-[#c9a962]" size={32} />
              </div>
              <h3 className="font-display text-2xl text-[#dbd0c2] mb-4">Zelle</h3>
              <p className="text-[#c9a962] font-mono text-lg mb-4 break-all">
                Chifuninthecityllc@gmail.com
              </p>
              <p className="text-[#dbd0c2]/60 text-sm">
                Direct bank transfer via Zelle
              </p>
            </article>

            {/* Seed & Spark */}
            <article className="donation-card rounded-sm" data-testid="seedspark-option" role="listitem">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#c9a962]/20 flex items-center justify-center" aria-hidden="true">
                <Heart className="text-[#c9a962]" size={32} />
              </div>
              <h3 className="font-display text-2xl text-[#dbd0c2] mb-4">Seed&Spark</h3>
              <a 
                href="https://seedandspark.com/fund/corporatechaos#story"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#c9a962] hover:underline inline-flex items-center gap-2 mb-4"
                data-testid="seedspark-donate-link"
                aria-label="Visit Seed and Spark Campaign (opens in new tab)"
              >
                Visit Campaign <ExternalLink size={16} aria-hidden="true" />
              </a>
              <p className="text-[#dbd0c2]/60 text-sm">
                Be part of the story, crowdfunding our independent series today
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Budget Breakdown with Deadline Reference */}
      <section className="py-20 px-6" data-testid="budget-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl text-[#dbd0c2] mb-4">Production Budget</h2>
            <p className="text-[#c9a962] text-3xl font-display">Total: $27,375</p>
            <p className="text-[#dbd0c2]/60 mt-2">Here&apos;s where your contributions go</p>
            {/* Deadline reminder near budget */}
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[#3a1f1f]/50 border border-[#c9a962]/30 rounded-sm">
              <Clock className="text-[#c9a962]" size={16} />
              <span className="text-[#dbd0c2]/80 text-sm">
                Fund by <span className="text-[#c9a962] font-semibold">July 10, 2026</span> to bring this to life
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {budgetItems.map((item, index) => (
              <div key={index} className="budget-item" data-testid={`budget-item-${item.name.toLowerCase().replace(' ', '-')}`}>
                <div className="flex justify-between items-center">
                  <span className="text-[#dbd0c2] font-medium">{item.name}</span>
                  <div className="text-right">
                    <span className="text-[#c9a962] font-semibold text-lg">{item.percentage}%</span>
                    <span className="text-[#dbd0c2]/60 ml-4">${item.amount.toLocaleString()}</span>
                  </div>
                </div>
                {/* 3D Progress Bar */}
                <div className="mt-3 budget-bar-container">
                  <div className="budget-bar-track">
                    <div 
                      className="budget-bar-fill"
                      style={{ width: `${item.percentage}%` }}
                    >
                      <div className="budget-bar-shine"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thank You */}
      <section className="py-16 px-6 bg-[#0f0d0c] text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display text-3xl text-[#dbd0c2] mb-4">Thank You</h2>
          <p className="text-[#dbd0c2]/70">
            Every contribution, no matter the size, helps bring Corporate Chaos to life. 
            Your support means the world to our team and the stories we&apos;re telling.
          </p>
        </div>
      </section>
    </main>
  );
};

// Contact Page with Netlify Form
const ContactPage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    const form = e.target;
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(new FormData(form)).toString(),
    })
      .then((response) => {
        if (response.ok) {
          setFormSubmitted(true);
        } else {
          throw new Error('Form submission failed');
        }
      })
      .catch((error) => {
        console.error('Form submission error:', error);
        setSubmitError('There was an error sending your message. Please try again or email directly.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <main className="page-transition pt-24" data-testid="contact-page" id="main-content" role="main">
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#c9a962] uppercase tracking-[4px] text-sm mb-4">Connect</p>
            <h1 className="font-display text-5xl md:text-6xl text-gold-gradient mb-6">
              Get in Touch
            </h1>
            <div className="section-divider mb-8"></div>
            <p className="text-[#dbd0c2]/70 max-w-2xl mx-auto">
              Available for modeling campaigns, brand collaborations, and creative projects. 
              Let&apos;s create something extraordinary together.
            </p>
          </div>

          {formSubmitted ? (
            <div className="card-heritage rounded-sm p-12 text-center" data-testid="form-success">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#c9a962]/20 flex items-center justify-center">
                <Heart className="text-[#c9a962]" size={40} />
              </div>
              <h2 className="font-display text-3xl text-[#dbd0c2] mb-4">Message Sent!</h2>
              <p className="text-[#dbd0c2]/70">
                Thank you for reaching out. I&apos;ll get back to you as soon as possible.
              </p>
            </div>
          ) : (
            <form 
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="card-heritage rounded-sm p-8 md:p-12"
              data-testid="contact-form"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>Don&apos;t fill this out: <input name="bot-field" /></label>
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-[#dbd0c2]/80 text-sm mb-2 uppercase tracking-wider">
                    Name *
                  </label>
                  <input 
                    type="text" 
                    id="name"
                    name="name" 
                    required 
                    className="form-input"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-[#dbd0c2]/80 text-sm mb-2 uppercase tracking-wider">
                    Email *
                  </label>
                  <input 
                    type="email" 
                    id="email"
                    name="email" 
                    required 
                    className="form-input"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="subject" className="block text-[#dbd0c2]/80 text-sm mb-2 uppercase tracking-wider">
                  Subject *
                </label>
                <select 
                  id="subject"
                  name="subject" 
                  required 
                  className="form-input"
                  data-testid="contact-subject-select"
                >
                  <option value="">Select an inquiry type</option>
                  <option value="modeling">Modeling Inquiry</option>
                  <option value="collaboration">Brand Collaboration</option>
                  <option value="corporate-chaos">Corporate Chaos</option>
                  <option value="press">Press / Media</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-8">
                <label htmlFor="message" className="block text-[#dbd0c2]/80 text-sm mb-2 uppercase tracking-wider">
                  Message *
                </label>
                <textarea 
                  id="message"
                  name="message" 
                  required 
                  rows="6" 
                  className="form-input resize-none"
                  placeholder="Tell me about your project or inquiry..."
                  data-testid="contact-message-textarea"
                ></textarea>
              </div>

              {submitError && (
                <div className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-sm text-red-300 text-sm" role="alert">
                  {submitError}
                </div>
              )}

              <button 
                type="submit" 
                className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}

          {/* Additional Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-[#dbd0c2]/60">
              For urgent inquiries: <span className="text-[#c9a962]">Chifuninthecityllc@gmail.com</span>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="App min-h-screen bg-[#1a1614]">
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/modeling" element={<ModelingPage />} />
          <Route path="/corporate-chaos" element={<CorporateChaosPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;